import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { localePath } from "@/i18n/localePath";

export const LandingNavbar: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lp = (path: string) => localePath(path, i18n.language);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [mobileOpen, setMobileOpen] = useState(false);

  const NAV_LINKS = [
    { label: t('nav.modes'), href: "#modes" },
    { label: t('nav.features'), href: "#features" },
    { label: t('nav.faq'), href: "#faq" },
    { label: t('nav.premium'), href: "/pricing", external: true },
    { label: t('nav.garden'), href: "/garden", external: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setActiveSection(id);
            if (id === "hero") {
              window.history.replaceState(null, "", window.location.pathname);
            } else {
              window.history.replaceState(null, "", `#${id}`);
            }
          }
        });
      },
      { rootMargin: "-50% 0px -50% 0px" }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, []);

  const handleNavClick = useCallback(
    (href: string, external?: boolean) => {
      setMobileOpen(false);
      if (external) {
        navigate(href);
        return;
      }
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    },
    [navigate]
  );

  const isActive = (href: string) => {
    if (href.startsWith("/")) return false;
    return activeSection === href.slice(1);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 30 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "shadow-sm" : "bg-transparent"}`}
        style={
          scrolled
            ? {
                background: "rgba(255,248,240,0.85)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                borderBottom: "1px solid rgba(255,200,150,0.1)",
              }
            : undefined
        }
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <motion.div
            className="flex items-center gap-1.5 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <span className="font-bold text-base text-foreground tracking-[1px]">PETERNITY</span>
            <span className="text-sm">🐾</span>
          </motion.div>

          <div className="hidden sm:flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href, link.external)}
                className="relative text-sm font-medium transition-colors py-1"
                style={{
                  color: isActive(link.href) ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                }}
              >
                {link.label}
                {isActive(link.href) && (
                  <motion.div
                    layoutId="nav-dot"
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-[6px] h-[6px] rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => navigate(lp("/auth"))}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('nav.sign_in')}
            </button>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button onClick={() => navigate(lp("/auth"))} size="sm" className="rounded-xl font-bold shadow-sm">
                {t('nav.get_started')}
              </Button>
            </motion.div>
          </div>

          <button
            className="sm:hidden p-2 rounded-xl hover:bg-foreground/5 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? t('nav.close_menu') : t('nav.open_menu')}
          >
            {mobileOpen ? <X className="h-5 w-5 text-foreground" /> : <Menu className="h-5 w-5 text-foreground" />}
          </button>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 left-0 right-0 z-50 px-4 pb-4"
          >
            <div
              className="rounded-2xl p-4 space-y-1 shadow-warm-lg"
              style={{
                background: "rgba(255,248,240,0.95)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: "1px solid rgba(255,200,150,0.15)",
              }}
            >
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href, link.external)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive(link.href) ? "bg-primary/10 text-primary" : "text-foreground hover:bg-foreground/5"
                  }`}
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-2 border-t border-border/30 mt-2">
                <div className="flex justify-center mb-3">
                  <LanguageSwitcher />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setMobileOpen(false); navigate(lp("/auth")); }}
                    className="flex-1 py-2.5 text-sm font-medium text-muted-foreground"
                  >
                    {t('nav.sign_in')}
                  </button>
                  <Button
                    onClick={() => { setMobileOpen(false); navigate(lp("/auth")); }}
                    size="sm"
                    className="flex-1 rounded-xl font-bold"
                  >
                    {t('nav.get_started')}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
